import { useState, useEffect, Fragment } from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/lib/utils";
import { LayoutDashboard, Users, MessageSquare, LogOut, CreditCard, ExternalLink, Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { lockBodyScroll, unlockBodyScroll } from "@/lib/bodyScrollLock";
import logoIcon from "@/assets/logo.svg";

export type PageId = "dashboard" | "users" | "settings";

export function AppLayout({
  page,
  onNavigate,
  children,
}: {
  page: PageId;
  onNavigate: (p: PageId) => void;
  children: React.ReactNode;
}) {
  const { user, company, plan, logout } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSales = user?.role === "sales";

  useEffect(() => {
    const lockId = "app:mobile-menu";
    if (mobileOpen) lockBodyScroll(lockId);
    else unlockBodyScroll(lockId);
    return () => {
      unlockBodyScroll(lockId);
    };
  }, [mobileOpen]);

  const nav = [
    { id: "dashboard" as const, label: "業務報告書一覧", icon: LayoutDashboard },
    { id: "users" as const, label: "メンバー", icon: Users },
    { id: "settings" as const, label: "メッセージ設定", icon: MessageSquare },
  ];

  async function handleUpgrade() {
    setCheckoutLoading(true);
    try {
      const { url } = await api.billing.createCheckoutSession();
      window.location.href = url;
    } catch (e) {
      console.error(e);
      setCheckoutLoading(false);
    }
  }

  async function handleBillingPortal() {
    setPortalLoading(true);
    try {
      const { url } = await api.billing.createPortalSession();
      window.location.href = url;
    } catch (e) {
      console.error(e);
      setPortalLoading(false);
    }
  }

  function handleNavClick(id: PageId) {
    if (isSales && id !== "dashboard") return;
    onNavigate(id);
    setMobileOpen(false);
  }

  const sidebarContent = (
    <>
      <div className="p-6 border-b border-border/80">
        <img src={logoIcon} alt="業務報告AI" className="h-6 w-auto" />
        <p className="text-xs text-muted-foreground mt-0.5">{company?.name ?? "管理画面"}</p>
      </div>
      <nav className="p-3 flex flex-col gap-0.5">
        {nav.map(({ id, label, icon: Icon }) => {
          const disabled = isSales && id !== "dashboard";
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleNavClick(id)}
              disabled={disabled}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                disabled && "cursor-not-allowed opacity-60",
                page === id && !disabled
                  ? "bg-primary/10 text-primary"
                  : disabled
                    ? "text-muted-foreground"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
            >
              <Icon className="size-4 shrink-0" />
              {label}
            </button>
          );
        })}
      </nav>
      <div className="mt-auto p-3 border-t border-border/80 space-y-2">
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <CreditCard className="size-3.5" />
          {plan?.name ?? "Free"} プラン
        </div>
        {!isSales && (plan?.id === "free" ? (
          <Button variant="outline" size="sm" className="w-full gap-1.5 rounded-lg" onClick={handleUpgrade} disabled={checkoutLoading}>
            <ExternalLink className="size-3.5" />
            {checkoutLoading ? "移動中…" : "Premium へ"}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="w-full gap-1.5 rounded-lg text-muted-foreground" onClick={handleBillingPortal} disabled={portalLoading}>
            <ExternalLink className="size-3.5" />
            {portalLoading ? "移動中…" : "請求管理"}
          </Button>
        ))}
        <Button variant="ghost" size="sm" className="w-full gap-2 rounded-lg text-muted-foreground" onClick={() => { logout(); setMobileOpen(false); }}>
          <LogOut className="size-4" />
          ログアウト
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* モバイル: ヘッダー + ハンバーガー */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 border-b border-border/80 bg-card/80 backdrop-blur flex items-center justify-between px-4">
        <img src={logoIcon} alt="業務報告AI" className="h-5 w-auto" />
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          aria-label="メニューを開く"
        >
          <Menu className="size-6" />
        </button>
      </header>

      {/* モバイル: サイドバーオーバーレイ */}
      <AnimatePresence>
        {mobileOpen && (
          <Fragment key="mobile-menu">
            <motion.div
              role="presentation"
              className="fixed inset-0 z-50 bg-black/50 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              className="fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85vw] border-r border-border/80 bg-card flex flex-col lg:hidden shadow-xl"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.2 }}
            >
              <div className="flex items-center justify-between p-4 border-b border-border/80">
                <span className="text-sm font-medium">メニュー</span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-lg text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  aria-label="メニューを閉じる"
                >
                  <X className="size-5" />
                </button>
              </div>
              <div className="flex flex-col flex-1 overflow-auto">
                {sidebarContent}
              </div>
            </motion.aside>
          </Fragment>
        )}
      </AnimatePresence>

      {/* デスクトップ: 常時表示サイドバー */}
      <aside className="hidden lg:flex w-56 border-r border-border/80 bg-card/40 flex-col shrink-0 h-screen sticky top-0">
        {sidebarContent}
      </aside>

      <main className="flex-1 overflow-auto pt-14 lg:pt-0 min-h-screen">
        <motion.div
          key={page}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="p-6 lg:p-8 max-w-5xl mx-auto"
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}
